import React, { useCallback, useEffect, useRef } from 'react';
/**
 * FormContent – the "Talk to Nathan" form, what every "Talk to us" opens.
 *
 * Three shapes on one anatomy (`BookingModal.anatomy.tsx`):
 * - **Marketing** (and a consultation offer): business email, optional name,
 *   optional note, "Send message"; the footer's "Book a 15m call instead"
 *   switches to the calendar view without submitting.
 * - **Unlocked report**: only the optional note. The unlock already gave us the
 *   email and the report names the practice, so the first tap can send; the
 *   server takes the reply address from the check's unlock lead.
 * - **Manual review** (a failed check): location, email, name, no footer.
 *
 * The form is disabled during submission (status === 'loading').
 *
 * @module
 */

import type { FormContentProps } from './BookingModal.types';
import { MailIcon, UserIcon, MessageIcon, LoadingSpinner } from './BookingModal.icons';
import { CallLink, ModalFooter, ModalHeader } from './BookingModal.anatomy';
import {
  BOOKING_CALL_INSTEAD_LABEL,
  BOOKING_MODAL_LEDE,
  BOOKING_MODAL_TITLE,
  BOOKING_NOTE_LABEL,
  BOOKING_REQUEST_CTA_LABEL,
} from '@/lib/conversion-copy';

const INPUT_CLASSES =
  'w-full rounded-lg border bg-white py-3 pl-11 pr-4 text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500';
const INPUT_OK = 'border-slate-300 hover:border-slate-400 focus:border-[#B45309] focus:ring-[#B45309]/20';
const INPUT_ERROR = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';
const LABEL_CLASSES = 'mb-1.5 block text-sm font-medium text-slate-700';
const NOTE_MAX_HEIGHT = 172;

export const REPORT_NOTE_PLACEHOLDER = 'Ask about a finding, or say hi';
export const NOTE_PLACEHOLDER = 'Include your website if you have one';

/** The caption under a report form's send button: where the reply goes. */
export function reportReplyCaption(replyEmail?: string): string {
  return replyEmail ? `Nathan will reply to ${replyEmail}` : 'Nathan will reply to the email you unlocked this report with';
}

export function FormContent({
  request,
  report,
  location,
  onLocationChange,
  message,
  onMessageChange,
  onBookCall,
  email,
  name,
  honeypot,
  status,
  error,
  onEmailChange,
  onNameChange,
  onHoneypotChange,
  onSubmit,
}: FormContentProps): React.ReactElement {
  const isSubmitting = status === 'loading';
  const manualReview = request?.kind === 'manual-review';

  return (
    <>
      <ModalHeader
        title={manualReview ? 'Request a manual review' : BOOKING_MODAL_TITLE}
        lede={
          manualReview
            ? 'Tell us where your business is based. We’ll review your website and follow up by email.'
            : BOOKING_MODAL_LEDE
        }
      />

      <form onSubmit={onSubmit} className="grid min-h-0 gap-5 overflow-y-auto px-5 pb-6 pt-5 sm:px-8 sm:py-6">
        {manualReview && (
          <>
            <p className="break-all text-sm text-slate-600">
              Website
              <br />
              <strong className="font-medium text-slate-900">{request.website}</strong>
            </p>
            <div>
              <label htmlFor="booking-location" className={LABEL_CLASSES}>
                Business city and state
              </label>
              <input
                id="booking-location"
                value={location ?? ''}
                onChange={(e) => onLocationChange?.(e.target.value)}
                required
                maxLength={150}
                disabled={isSubmitting}
                placeholder="Austin, TX"
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-[#B45309] focus:outline-none focus:ring-2 focus:ring-[#B45309]/20"
              />
            </div>
          </>
        )}

        {/* The report already has the email from its unlock; asking again is friction. */}
        {!report && (
          <>
            <div>
              <label htmlFor="booking-email" className={LABEL_CLASSES}>
                Business email
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <MailIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="booking-email"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'booking-email-error' : undefined}
                  className={`${INPUT_CLASSES} ${error ? INPUT_ERROR : INPUT_OK}`}
                />
              </div>
            </div>

            <div>
              <label htmlFor="booking-name" className={LABEL_CLASSES}>
                Your name <span className="text-slate-400">(optional)</span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <UserIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="booking-name"
                  type="text"
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Jane Smith"
                  disabled={isSubmitting}
                  autoComplete="name"
                  className={`${INPUT_CLASSES} ${INPUT_OK}`}
                />
              </div>
            </div>
          </>
        )}

        {!manualReview && (
          <NoteField
            value={message}
            onChange={onMessageChange}
            disabled={isSubmitting}
            placeholder={report ? REPORT_NOTE_PLACEHOLDER : NOTE_PLACEHOLDER}
          />
        )}

        {/* Honeypot field (hidden from real users) */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => onHoneypotChange(e.target.value)}
          className="absolute -left-[9999px]"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        {error && (
          <p id="booking-email-error" className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="mt-1 grid gap-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full bg-[#B45309] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-[#92400E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B45309] focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner />
                Sending...
              </span>
            ) : manualReview ? (
              'Send review request'
            ) : (
              BOOKING_REQUEST_CTA_LABEL
            )}
          </button>
          {report && <p className="text-balance text-center text-xs text-slate-500 [overflow-wrap:anywhere]">{reportReplyCaption(report.replyEmail)}</p>}
        </div>
      </form>

      {onBookCall && (
        <ModalFooter>
          <CallLink label={BOOKING_CALL_INSTEAD_LABEL} onClick={onBookCall} />
        </ModalFooter>
      )}
    </>
  );
}

/**
 * The optional note. Grows a line at a time to about six lines, then scrolls:
 * CSS `field-sizing: content` where supported, a scrollHeight fallback elsewhere.
 */
function NoteField({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder: string;
}): React.ReactElement {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el || (typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content'))) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, NOTE_MAX_HEIGHT)}px`;
  }, []);
  // Values kept across a calendar round trip need sizing on remount, not just on input.
  useEffect(fit, [fit, value]);

  return (
    <div>
      <label htmlFor="booking-message" className={LABEL_CLASSES}>
        {BOOKING_NOTE_LABEL} <span className="text-slate-400">(optional)</span>
      </label>
      <div className="relative">
        <MessageIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
        <textarea
          ref={ref}
          id="booking-message"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={1000}
          rows={2}
          disabled={disabled}
          placeholder={placeholder}
          className={`${INPUT_CLASSES} ${INPUT_OK} block max-h-[172px] min-h-[76px] resize-none [field-sizing:content]`}
        />
      </div>
    </div>
  );
}
